import React, { useState, useContext, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { AuthContext } from '@/App';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { ArrowLeft, Download, Calendar, Users, Eye, MousePointer, TrendingUp } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AnalyticsReport = () => {
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [report, setReport] = useState(null);
  const [error, setError] = useState(null);
  
  // Date range state
  const today = new Date().toISOString().split('T')[0];
  const thirtyDaysAgo = new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0];
  const [startDate, setStartDate] = useState(thirtyDaysAgo);
  const [endDate, setEndDate] = useState(today);

  useEffect(() => {
    if (!user || user.role !== 'admin') {
      navigate('/');
    }
  }, [user, navigate]);

  const fetchReport = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await axios.get(`${API}/analytics/report`, {
        params: { start_date: startDate, end_date: endDate, format: 'json' },
        withCredentials: true
      });
      setReport(response.data);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to fetch report');
    } finally {
      setLoading(false);
    }
  };

  const downloadCSV = async () => {
    try {
      const response = await axios.get(`${API}/analytics/report`, {
        params: { start_date: startDate, end_date: endDate, format: 'csv' },
        withCredentials: true,
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `analytics_report_${startDate}_${endDate}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (err) {
      alert('Failed to download report');
    }
  };

  const downloadRawData = async () => {
    try {
      const response = await axios.get(`${API}/analytics/export`, {
        params: { start_date: startDate, end_date: endDate },
        withCredentials: true,
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `analytics_raw_${startDate}_${endDate}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (err) {
      alert('Failed to download raw data');
    }
  };

  if (!user || user.role !== 'admin') {
    return null;
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-emerald-50 to-slate-100 p-4 sm:p-6 lg:p-8">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-4">
            <Button variant="outline" onClick={() => navigate('/dashboard')}>
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Dashboard
            </Button>
            <h1 className="text-3xl font-bold text-slate-900" style={{ fontFamily: 'Playfair Display, serif' }}>
              Analytics Report
            </h1>
          </div>
        </div>

        {/* Date Range Selector */}
        <Card className="mb-6">
          <CardContent className="p-6">
            <div className="flex flex-wrap items-end gap-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Start Date</label>
                <input
                  type="date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  className="px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-emerald-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">End Date</label>
                <input
                  type="date"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                  className="px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-emerald-500"
                />
              </div>
              <Button onClick={fetchReport} disabled={loading} className="bg-emerald-700 hover:bg-emerald-800">
                <TrendingUp className="w-4 h-4 mr-2" />
                {loading ? 'Loading...' : 'Generate Report'}
              </Button>
              <Button onClick={downloadCSV} variant="outline" disabled={!report}>
                <Download className="w-4 h-4 mr-2" />
                Download CSV
              </Button>
              <Button onClick={downloadRawData} variant="outline">
                <Download className="w-4 h-4 mr-2" />
                Export Raw Data
              </Button>
            </div>
          </CardContent>
        </Card>

        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-6">
            {error}
          </div>
        )}

        {report && (
          <>
            {/* Summary Cards */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
              <Card>
                <CardContent className="p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-slate-500">Total Page Views</p>
                      <p className="text-3xl font-bold text-slate-900">{report.summary.total_page_views.toLocaleString()}</p>
                    </div>
                    <Eye className="w-10 h-10 text-emerald-600 opacity-50" />
                  </div>
                </CardContent>
              </Card>
              
              <Card>
                <CardContent className="p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-slate-500">Unique Visitors</p>
                      <p className="text-3xl font-bold text-slate-900">{report.summary.unique_visitors.toLocaleString()}</p>
                    </div>
                    <Users className="w-10 h-10 text-blue-600 opacity-50" />
                  </div>
                </CardContent>
              </Card>
              
              <Card>
                <CardContent className="p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-slate-500">Sessions</p>
                      <p className="text-3xl font-bold text-slate-900">{report.summary.unique_sessions.toLocaleString()}</p>
                    </div>
                    <MousePointer className="w-10 h-10 text-purple-600 opacity-50" />
                  </div>
                </CardContent>
              </Card>
              
              <Card>
                <CardContent className="p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-slate-500">Pages/Session</p>
                      <p className="text-3xl font-bold text-slate-900">{report.summary.avg_pages_per_session}</p>
                    </div>
                    <Calendar className="w-10 h-10 text-orange-600 opacity-50" />
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Detailed Sections */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Traffic Sources */}
              <Card>
                <CardHeader>
                  <CardTitle>Traffic Sources</CardTitle>
                </CardHeader>
                <CardContent>
                  {Object.keys(report.traffic_sources).length > 0 ? (
                    <div className="space-y-3">
                      {Object.entries(report.traffic_sources).map(([source, views]) => (
                        <div key={source} className="flex items-center justify-between">
                          <span className="text-slate-700 truncate max-w-[200px]">{source}</span>
                          <div className="flex items-center gap-2">
                            <div className="w-32 bg-slate-200 rounded-full h-2">
                              <div 
                                className="bg-emerald-600 h-2 rounded-full" 
                                style={{ width: `${(views / report.summary.total_page_views) * 100}%` }}
                              />
                            </div>
                            <span className="text-sm font-medium text-slate-900 w-16 text-right">{views}</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-slate-500">No traffic source data</p>
                  )}
                </CardContent>
              </Card>

              {/* Top Pages */}
              <Card>
                <CardHeader>
                  <CardTitle>Top Pages</CardTitle>
                </CardHeader>
                <CardContent>
                  {Object.keys(report.top_pages).length > 0 ? (
                    <div className="space-y-3">
                      {Object.entries(report.top_pages).map(([page, views]) => {
                        const pageName = page.replace('https://www.traininjapan.com', '').replace('https://traininjapan.com', '') || '/';
                        return (
                          <div key={page} className="flex items-center justify-between">
                            <span className="text-slate-700 truncate max-w-[200px]" title={page}>{pageName}</span>
                            <div className="flex items-center gap-2">
                              <div className="w-32 bg-slate-200 rounded-full h-2">
                                <div 
                                  className="bg-blue-600 h-2 rounded-full" 
                                  style={{ width: `${(views / report.summary.total_page_views) * 100}%` }}
                                />
                              </div>
                              <span className="text-sm font-medium text-slate-900 w-16 text-right">{views}</span>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    <p className="text-slate-500">No page data</p>
                  )}
                </CardContent>
              </Card>

              {/* UTM Campaigns */}
              <Card>
                <CardHeader>
                  <CardTitle>UTM Campaigns</CardTitle>
                </CardHeader>
                <CardContent>
                  {Object.keys(report.utm_campaigns).length > 0 ? (
                    <div className="space-y-3">
                      {Object.entries(report.utm_campaigns).map(([campaign, views]) => (
                        <div key={campaign} className="flex items-center justify-between">
                          <span className="text-slate-700 truncate max-w-[200px]">{campaign}</span>
                          <div className="flex items-center gap-2">
                            <div className="w-32 bg-slate-200 rounded-full h-2">
                              <div 
                                className="bg-purple-600 h-2 rounded-full" 
                                style={{ width: `${(views / report.summary.total_page_views) * 100}%` }}
                              />
                            </div>
                            <span className="text-sm font-medium text-slate-900 w-16 text-right">{views}</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-slate-500">No UTM campaign data (TikTok clicks will appear here when tracked)</p>
                  )}
                </CardContent>
              </Card>

              {/* Daily Views Chart */}
              <Card>
                <CardHeader>
                  <CardTitle>Daily Page Views</CardTitle>
                </CardHeader>
                <CardContent>
                  {Object.keys(report.daily_views).length > 0 ? (
                    <div className="space-y-2">
                      {Object.entries(report.daily_views).slice(-14).map(([date, views]) => {
                        const maxViews = Math.max(...Object.values(report.daily_views));
                        return (
                          <div key={date} className="flex items-center gap-2">
                            <span className="text-xs text-slate-500 w-20">{date.slice(5)}</span>
                            <div className="flex-1 bg-slate-200 rounded-full h-4">
                              <div 
                                className="bg-gradient-to-r from-emerald-500 to-emerald-600 h-4 rounded-full flex items-center justify-end pr-2" 
                                style={{ width: `${Math.max((views / maxViews) * 100, 10)}%` }}
                              >
                                <span className="text-xs text-white font-medium">{views}</span>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    <p className="text-slate-500">No daily view data</p>
                  )}
                </CardContent>
              </Card>
            </div>

            {/* Report Info */}
            <div className="mt-6 text-sm text-slate-500 text-center">
              Report generated: {new Date(report.report_generated).toLocaleString()} | 
              Date range: {report.date_range.start} to {report.date_range.end}
            </div>
          </>
        )}

        {!report && !loading && (
          <Card>
            <CardContent className="p-12 text-center">
              <TrendingUp className="w-16 h-16 text-slate-300 mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-slate-700 mb-2">Generate Your Analytics Report</h3>
              <p className="text-slate-500 mb-4">Select a date range and click "Generate Report" to view your analytics data.</p>
              <p className="text-sm text-slate-400">You can also export raw data as CSV for further analysis.</p>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
};

export default AnalyticsReport;
